import { ClientEvents } from 'common/events/ClientEvents';
import { isNil } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useShowLoginOrSignUpModal } from 'src/auth/modal/useShowLoginOrSignUpModal';
import { ShefRosterStatus, useFollowShefMutation, useUnfollowShefMutation } from 'src/gqlReactTypings.generated.d';
import {
  showSavedToFavoritesToast,
  showUnfavoritedConfirmationToast,
} from 'src/pages/consumer/favorite-food-item/FavoriteToasts';
import { useFavoritesV2Enabled } from 'src/pages/consumer/favorite-food-item/hooks/useFavoritesV2Enabled';
import { FollowColors } from 'src/shared/design-system/FollowShef';
import { useCurrentUser } from 'src/shared/hooks/useCurrentUserHook';
import { useTracker } from 'src/shared/hooks/useTracker';
import Notifications from 'src/shared/Notifications';
import { useShefRosterStoreSelectors } from 'src/ztore/shef-roster-store';
import { match } from 'ts-pattern';
import { FollowButtonProps } from '../types';
import { useIsFollowing } from './useIsFollowing';
import { useShowModal } from './useShowModal';

/**
 * Hook function for the follow button.
 */
export const useFollowButton = (props: FollowButtonProps) => {
  const {
    color,
    followingColor,
    enableFollowModal = true,
    hideIfFollowing,
    imageUrl,
    name,
    smallScreen,
    shefId,
    showNameOnButton,
    showUnfollowTooltip = true,
    location,
    onAfterUnfavorite,
    onAfterUndo,
  } = props;
  const { show: showLoginModal } = useShowLoginOrSignUpModal();
  const [currentUser] = useCurrentUser();
  const followQuery = useIsFollowing({ shefId });
  const [originalStatus, setOriginalStatus] = useState<ShefRosterStatus | undefined>(undefined);

  const { shefRosterIndex, updateShefRosterStats, isShefRosterLoading } = useShefRosterStoreSelectors();
  const isFollowing = isNil(shefRosterIndex[shefId])
    ? followQuery.isFollowing
    : shefRosterIndex[shefId] === ShefRosterStatus.Favorite;

  useEffect(() => {
    if (!isShefRosterLoading) {
      setOriginalStatus(shefRosterIndex[shefId]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only setting original status on first load
  }, [shefId, isShefRosterLoading]);

  const numFollowedShefs = Object.values(shefRosterIndex).reduce(
    (count, status) => (count + status === ShefRosterStatus.Favorite ? 1 : 0),
    0
  );

  const { isShowing, isShowingTooltip, onClose, publicName, showModal } = useShowModal({
    enableFollowModal,
    name,
    numFollowedShefs: isNil(shefRosterIndex[shefId]) ? followQuery.followedShefs.length : numFollowedShefs,
  });

  const [optimisticFollowShef, setOptimisticFollowShef] = useState(false);
  const [followShef] = useFollowShefMutation();
  const [unfollowShef] = useUnfollowShefMutation();
  const tracker = useTracker();
  const favoritesV2Enabled = useFavoritesV2Enabled();

  // Always follows (not toggle) so the Undo closure stays correct after rapid re-toggles.
  const refollow = useCallback(async () => {
    try {
      followQuery.setIsFollowing(true);
      updateShefRosterStats([{ shefId, status: ShefRosterStatus.Favorite }]);
      setOptimisticFollowShef(true);
      onAfterUndo?.(shefId);
      await followShef({ variables: { shefId } });
    } catch (e) {
      updateShefRosterStats([{ shefId, status: ShefRosterStatus.None }]);
      Notifications.error('Error following shef. Refresh page and try again.');
    }
  }, [followQuery, followShef, onAfterUndo, shefId, updateShefRosterStats]);

  const onFollow = useCallback(async () => {
    const followEventData = { shefId, location };
    const mutationProps = { variables: { shefId } };
    const nextRosterStatus = getNextStatus(isFollowing, originalStatus);
    const willFavorite = nextRosterStatus === ShefRosterStatus.Favorite;
    const followEvent = willFavorite ? ClientEvents.FOLLOW_SHEF_START : ClientEvents.FOLLOW_SHEF_STOP;
    const favoriteEvent = willFavorite ? ClientEvents.SHEF_FAVORITE_ADDED : ClientEvents.SHEF_FAVORITE_REMOVED;
    const mutation = willFavorite ? followShef : unfollowShef;

    try {
      followQuery.setIsFollowing(willFavorite);
      updateShefRosterStats([{ shefId, status: nextRosterStatus }]);
      setOptimisticFollowShef(willFavorite);
      if (!willFavorite) onAfterUnfavorite?.(shefId);
      await mutation(mutationProps);
      tracker.track(followEvent, followEventData);
      // Dual-emit under v2 — keeps legacy follow events firing for control.
      if (favoritesV2Enabled) {
        tracker.track(favoriteEvent, { shefId, shefName: name, surface: location });
      }
      if (willFavorite) {
        if (favoritesV2Enabled) {
          showSavedToFavoritesToast({ subject: 'Shef' });
        } else {
          showModal();
        }
      } else if (favoritesV2Enabled) {
        showUnfavoritedConfirmationToast({ subject: 'Shef', onUndo: refollow });
      }
    } catch (e) {
      updateShefRosterStats([{ shefId, status: isFollowing ? ShefRosterStatus.Favorite : originalStatus! }]);
      Notifications.error(`Error ${willFavorite ? 'following' : 'unfollowing'} shef. Refresh page and try again.`);
    }
  }, [
    favoritesV2Enabled,
    followQuery,
    followShef,
    isFollowing,
    location,
    name,
    onAfterUnfavorite,
    originalStatus,
    shefId,
    showModal,
    tracker,
    refollow,
    unfollowShef,
    updateShefRosterStats,
  ]);

  const toggleFollow = async (e: any): Promise<boolean> => {
    if (!currentUser) {
      /** Do not trigger follow side effects for logged-out users */
      showLoginModal({
        entryHeader: 'Login or Signup to favorite Shefs',
        signUpEntryPoint: location,
        preventReloadOnLogin: true,
        onSuccessfulLogin: onFollow,
        signUpCTA: 'Sign Up to favorite',
        continueCTA: 'Continue to Favorite',
        loginCTA: 'Log in to Favorite',
        hideBanner: true,
        hideGuestOption: true,
      });
      return false;
    }
    /** Handle follow for logged-in users */
    e.stopPropagation();
    await onFollow();
    return true;
  };

  return {
    buttonProps: {
      color,
      followingColor: match({ showNameOnButton, smallScreen })
        .with({ showNameOnButton: true }, () => FollowColors.WHITE)
        .with({ smallScreen: true }, () => FollowColors.RED)
        .otherwise(() => followingColor ?? FollowColors.BLACK),
      iconOnly: match({ showNameOnButton, smallScreen })
        .with({ showNameOnButton: true }, () => false)
        .with({ showNameOnButton: true, smallScreen: true }, () => false)
        .with({ smallScreen: true }, () => true)
        .otherwise(() => false),
      isAnimated: followQuery.isAnimated,
      isFollowing,
      isFollowingLoading: isShefRosterLoading,
      name,
      onClick: toggleFollow,
      showUnfollowTooltip: showUnfollowTooltip && !optimisticFollowShef,
    },
    isHidden: hideIfFollowing && isFollowing,
    isModalShown: enableFollowModal,
    modalProps: {
      imageUrl: imageUrl ?? '',
      isShowing,
      onClose: () => {
        onClose();
      },
      publicName,
    },
    tooltipProps: {
      isOpen: isShowingTooltip,
    },
  };
};

const getNextStatus = (isFollowing: boolean, originalStatus: ShefRosterStatus | undefined): ShefRosterStatus => {
  if (isFollowing) {
    return originalStatus === ShefRosterStatus.Favorite || isNil(originalStatus)
      ? ShefRosterStatus.None
      : originalStatus;
  }
  return ShefRosterStatus.Favorite;
};
